<template>
  <LModal
    :visible="visible"
    class="sqdc-event-linkage-modal"
    size="xl"
    :title="modalTitle"
    ok-variant="success"
    :loading="loading"
    :submit="saveLinkages"
    @update:visible="emit('update:visible', $event)"
  >
    <template #title>
      <span class="linkage-heading">
        <span class="linkage-heading-main">
          {{ t("components.event-action.event.EventLinkageModal.title") }}
          <span class="linkage-heading-event">
            <span class="event-id" :style="eventIdStyle(event.typeId)">
              {{ sourceIdentifier }}
            </span>
            <span class="linkage-heading-type">{{ eventTypeName(event.typeId) }}</span>
          </span>
        </span>
        <span v-if="sourceSummary" class="linkage-heading-summary" :title="sourceSummary">
          {{ sourceSummary }}
        </span>
      </span>
    </template>
    <div v-if="event.originalBoardId == null" class="no-board-message">
      <i class="fas fa-exclamation-triangle" />
      {{ t("components.event-action.event.EventLinkageModal.noBoardConnection") }}
    </div>
    <div v-else class="linkage-content">
      <div class="linkage-filter-frame">
        <EventActionFilterStripe
          v-model:event-filters="eventFilters"
          v-model:action-filters="unusedActionFilters"
          v-model:quick-search="quickSearch"
          class="linkage-filter-stripe"
          :board-id="event.originalBoardId"
          :event-types="filterEventTypes"
          :event-count="availableEvents.length"
          event-only
        />
      </div>

      <div ref="eventList" class="event-list" role="listbox" aria-multiselectable="true">
        <LISAButton
          v-for="linkableEvent in visibleEvents"
          :key="linkableEvent.id"
          class="event-option"
          variant="neutral"
          text
          full-width
          :class="{ selected: selectedEventIds.has(linkableEvent.id) }"
          role="option"
          :aria-selected="selectedEventIds.has(linkableEvent.id)"
          @click="toggleEvent(linkableEvent.id)"
        >
          <span class="event-option-content">
            <LISACheckbox
              :model-value="selectedEventIds.has(linkableEvent.id)"
              :tabindex="-1"
              @click.stop
              @update:model-value="toggleEvent(linkableEvent.id)"
            />
            <span class="event-id" :style="eventIdStyle(linkableEvent.typeId)">
              {{ eventIdentifier(linkableEvent) }}
            </span>
            <span class="event-type">{{ eventTypeName(linkableEvent.typeId) }}</span>
            <span class="event-summary">{{ eventSummary(linkableEvent) }}</span>
            <span v-if="linkableEvent.deletedAt != null" class="event-status">
              {{ t("components.event-action.event.EventLinkageModal.archived") }}
            </span>
          </span>
        </LISAButton>

        <div v-if="queryLoading || loadingMore" class="event-list-state loading-state">
          <LISASpinner class="loading-spinner" size="lg" />
        </div>
        <div v-else-if="visibleEvents.length === 0 && !canLoadMore" class="event-list-state">
          {{ t("global.noData") }}
        </div>
      </div>
    </div>
  </LModal>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { useInfiniteScroll } from "@vueuse/core";
import { DateTime } from "luxon";
import { LISAButton, LISACheckbox, LISASpinner } from "@fi-solutions/lisa-ui";
import { useTranslation } from "@/language/translation";
import LModal from "@/components/LModal.vue";
import {
  EABFilterImplementationType,
  EABFilterType,
  PaginationOrder,
  SQDCEventActionTypeType,
  useEABLoadSiteEventsQuery,
  useGetSiteIdForBoardQuery,
  useSQDCActionTypesQuery,
  useSQDCEventLinkMutation,
  type EABLoadSiteEventsQuery,
  type EABFilter,
  type EABFilterInstance,
} from "@/graphql";
import notEmpty from "@/functions/notEmpty";
import EventActionFilterStripe from "./EventActionFilterStripe.vue";
import { useEABEventFieldDisplays } from "./EABEventFieldHelper";
import { useSQDCDisplayName } from "../../helper/SQDCDisplayNameHelper";
import { getEventPrefix, type EABEventType, type EABEventTypeType } from "./EventActionHelper";
import type { ActionFilters, EventFilters, FilterEventActionTypes } from "./filter/FilterTypes";

type SiteLinkableEvent = EABLoadSiteEventsQuery["SQDCEvents"][number];
type LinkableEvent = Pick<SiteLinkableEvent, "id" | "typeId"> &
  Partial<Omit<SiteLinkableEvent, "id" | "typeId">>;

const props = defineProps<{
  event: EABEventType;
  visible: boolean;
  eventTypes?: EABEventTypeType[];
}>();

const emit = defineEmits<{
  "update:visible": [visible: boolean];
}>();

const { t } = useTranslation();
const displayName = useSQDCDisplayName();
const quickSearch = ref("");
const selectedEventIds = ref<ReadonlySet<number>>(new Set());
const eventFilters = ref<EventFilters>(createEmptyEventFilters());
const unusedActionFilters = ref<ActionFilters>({
  updatedAt: DateTime.now(),
  actionFilters: [],
  type: EABFilterType.AND,
});

const { result: originBoardResult, loading: originBoardLoading } = useGetSiteIdForBoardQuery(
  computed(() => ({ boardId: props.event.originalBoardId ?? 0 })),
  computed(() => ({
    enabled: props.visible && props.event.originalBoardId != null,
    fetchPolicy: "cache-first" as const,
  })),
);
const siteId = computed(() => originBoardResult.value?.GetSQDCBoard.siteId);
const { result: siteEventTypesResult, loading: eventTypesLoading } = useSQDCActionTypesQuery(
  computed(() => ({
    siteIds: siteId.value == null ? [] : [siteId.value],
    types: [SQDCEventActionTypeType.EVENT],
  })),
  computed(() => ({
    enabled: props.visible && siteId.value != null,
    fetchPolicy: "cache-first" as const,
  })),
);
const eventTypes = computed(() => {
  const types = new Map((props.eventTypes ?? []).map((type) => [type.id, type]));
  for (const type of siteEventTypesResult.value?.SQDCEventActionTypes ?? []) {
    types.set(type.id, type);
  }
  return [...types.values()];
});
const filterEventTypes = computed<FilterEventActionTypes[]>(() =>
  eventTypes.value.map((type) => ({
    ...type,
    fields: type.SQDCEventActionTypeFields,
  })),
);
const sourceIdentifier = computed(
  () =>
    props.event.oldId ?? `${getEventPrefix(eventTypes.value, props.event.typeId)}${props.event.id}`,
);
const PAGE_SIZE = 50;

const filter = computed<EABFilterInstance | undefined>(() => {
  const eventOptions = eventFilters.value.eventFilters
    .map((eventFilter) => eventFilter.getEventFilter())
    .filter(notEmpty);
  const search = quickSearch.value.trim();
  const searchOption =
    search !== ""
      ? {
          filter: {
            type: EABFilterImplementationType.EVENT_FREE_TEXT,
            value: { value: search, negated: false },
          },
        }
      : undefined;
  const groups = [
    eventOptions.length > 0
      ? {
          operation: {
            type: eventFilters.value.type ?? EABFilterType.AND,
            filters: eventOptions,
          },
        }
      : undefined,
    searchOption,
  ].filter(notEmpty);

  if (groups.length === 0) {
    return undefined;
  }
  return {
    ignoreCreatedAt: new Date().toISOString(),
    filter: {
      type: EABFilterType.AND,
      filters: groups,
    } as EABFilter,
  };
});

const queryVariables = computed(() => ({
  siteId: siteId.value ?? 0,
  page: {
    cursor: undefined,
    order: PaginationOrder.DESC,
    take: PAGE_SIZE,
  },
  filter: filter.value,
  includeArchived: true,
}));
const {
  result,
  loading: queryLoading,
  fetchMore,
} = useEABLoadSiteEventsQuery(
  queryVariables,
  computed(() => ({
    enabled: props.visible && siteId.value != null,
    fetchPolicy: "cache-and-network" as const,
  })),
);
const { mutate: linkEvents, loading: linkLoading } = useSQDCEventLinkMutation();
const loading = computed(
  () =>
    originBoardLoading.value || eventTypesLoading.value || queryLoading.value || linkLoading.value,
);

const linkedEvents = computed<LinkableEvent[]>(() =>
  props.event.SQDCEventLinkageSources.map((linkage) => linkage.targetEvent).filter(notEmpty),
);
const availableEvents = computed<LinkableEvent[]>(() => {
  const events = new Map<number, LinkableEvent>();
  if (quickSearch.value.trim() === "") {
    for (const linkedEvent of linkedEvents.value) {
      events.set(linkedEvent.id, linkedEvent);
    }
  }
  for (const event of result.value?.SQDCEvents ?? []) {
    if (event.id !== props.event.id) {
      events.set(event.id, event);
    }
  }
  return [...events.values()];
});
const visibleEvents = availableEvents;
const displayEvents = computed<LinkableEvent[]>(() => [props.event, ...availableEvents.value]);
const { getDisplays: getEventFieldDisplays } = useEABEventFieldDisplays(displayEvents);
const sourceSummary = computed(() => eventSummary(props.event));
const modalTitle = computed(
  () =>
    `${t("components.event-action.event.EventLinkageModal.title")}: ${eventTypeName(props.event.typeId)} ${sourceIdentifier.value}`,
);

const canLoadMore = ref(true);
const eventList = useTemplateRef<HTMLElement>("eventList");
const { isLoading: loadingMore, reset: resetInfiniteScroll } = useInfiniteScroll(
  eventList,
  async () => {
    try {
      const loadedEvents = result.value?.SQDCEvents ?? [];
      const cursor = loadedEvents.at(-1)?.id;
      if (cursor == null || siteId.value == null) {
        canLoadMore.value = false;
        return;
      }
      const response = await fetchMore({
        variables: {
          siteId: siteId.value,
          page: {
            cursor: cursor.toString(),
            order: PaginationOrder.DESC,
            take: PAGE_SIZE,
          },
          filter: filter.value,
          includeArchived: true,
        },
      });
      if ((response?.data?.SQDCEvents.length ?? 0) < PAGE_SIZE) {
        canLoadMore.value = false;
      }
    } catch {
      canLoadMore.value = false;
    }
  },
  {
    distance: 120,
    canLoadMore: () => canLoadMore.value && !queryLoading.value,
  },
);

watch(
  () => props.visible,
  (visible) => {
    if (!visible) {
      return;
    }
    quickSearch.value = "";
    eventFilters.value = createEmptyEventFilters();
    selectedEventIds.value = new Set(
      props.event.SQDCEventLinkageSources.map((linkage) => linkage.targetEventId),
    );
  },
  { immediate: true },
);

watch([filter, siteId], () => {
  canLoadMore.value = true;
  resetInfiniteScroll();
});

function createEmptyEventFilters(): EventFilters {
  return {
    updatedAt: DateTime.now(),
    eventFilters: [],
    type: EABFilterType.AND,
  };
}

function toggleEvent(eventId: number) {
  const nextSelection = new Set(selectedEventIds.value);
  if (nextSelection.has(eventId)) {
    nextSelection.delete(eventId);
  } else {
    nextSelection.add(eventId);
  }
  selectedEventIds.value = nextSelection;
}

function eventIdentifier(event: Pick<LinkableEvent, "id" | "typeId" | "oldId">) {
  return event.oldId ?? `${getEventPrefix(eventTypes.value, event.typeId)}${event.id}`;
}

function eventTypeName(typeId: number) {
  const type = eventTypes.value.find((eventType) => eventType.id === typeId);
  if (type == null) {
    return t("global.na");
  }
  return displayName(type);
}

function eventSummary(event: LinkableEvent) {
  const eventType = eventTypes.value.find((type) => type.id === event.typeId);
  if (eventType == null) {
    return "";
  }

  const isAtCurrentSite =
    event.id === props.event.id ||
    (result.value?.SQDCEvents.some((siteEvent) => siteEvent.id === event.id) ?? false);
  const values = getEventFieldDisplays(
    event,
    eventType,
    isAtCurrentSite ? siteId.value : undefined,
  ).filter(({ value }) => value !== "");
  const showLabels = values.length > 1;
  return values.map(({ label, value }) => (showLabels ? `${label}: ${value}` : value)).join(" · ");
}

function eventIdStyle(typeId: number) {
  const color = eventTypes.value.find((eventType) => eventType.id === typeId)?.idColor;
  return color == null ? undefined : { borderColor: color, color };
}

async function saveLinkages() {
  await linkEvents({
    sourceEventId: props.event.id,
    targetEventIds: [...selectedEventIds.value],
  });
  return { success: true };
}
</script>

<style lang="scss" scoped>
.linkage-content {
  display: flex;
  min-height: 50vh;
  max-height: 65vh;
  flex-direction: column;
  gap: 0.75rem;
}

.linkage-heading {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.2rem;
}

.linkage-heading-main,
.linkage-heading-event {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.65rem;
}

.linkage-heading-event {
  font-weight: 600;
}

.linkage-heading .event-id {
  width: auto;
  flex: 0 0 auto;
}

.linkage-heading-type,
.linkage-heading-summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.linkage-heading-summary {
  color: var(--p-text-muted-color);
  font-size: 0.8rem;
  font-weight: 400;
}

.linkage-filter-frame {
  flex: 0 0 auto;
  padding: 0.5rem;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-color);
}

.linkage-filter-frame :deep(.linkage-filter-stripe) {
  margin: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
}

.event-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid var(--p-gray-200);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
}

.event-option {
  width: 100%;
  border-bottom: 1px solid var(--p-gray-100);
  border-radius: 0;
  color: var(--p-text-color);
}

.event-option :deep(.lisa-button__content),
.event-option :deep(.lisa-button__text),
.event-option :deep(.lisa-button__title),
.event-option :deep(.lisa-button__label) {
  width: 100%;
  justify-content: flex-start;
}

.event-option-content {
  display: grid;
  width: 100%;
  grid-template-columns: 2.25rem 6rem 10rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
}

.event-option:hover,
.event-option.selected {
  background: var(--p-highlight-background);
}

.event-id {
  width: 100%;
  padding: 0.15rem 0.4rem;
  border-left: 0.25rem solid var(--p-primary-color);
  font-weight: 700;
}

.event-type {
  font-weight: 600;
}

.event-summary {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.event-status {
  color: var(--p-text-muted-color);
  font-size: 0.8rem;
}

.event-list-state,
.no-board-message {
  display: flex;
  min-height: 12rem;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: var(--p-text-muted-color);
}

.loading-spinner {
  width: 2rem;
  height: 2rem;
}

:global(.sqdc-event-linkage-modal .p-dialog-title) {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

@media (max-width: 800px) {
  .event-option-content {
    grid-template-columns: auto auto minmax(0, 1fr);
  }

  .event-type,
  .event-status {
    display: none;
  }
}
</style>
